import { ANALYTICS_HEAP_IDS, formatHeapId } from '@/common/constants';
import { getHostnameFromStageAndTLD } from '@/common/helpers/requestHelpers';
import { Env } from '@/common/types';
import { ArrowChevronDownIcon } from '@eventbrite/marmalade/icons';
import { LOCALE_DEFAULTS } from '@eventbrite/webapp-locales';
import React, { useMemo } from 'react';
import styles from './CountrySelector.module.scss';

type LocaleDefault = (typeof LOCALE_DEFAULTS)[number];

export interface CountrySelectorProps {
  env: Env;
}

/**
 * Strips locale directory prefixes from pagePath
 * Removes patterns like /fr_CA/, /nl_BE/, /en_US/ etc from the beginning of the path
 */
const stripLocalePrefix = (pagePath: string): string => {
  // Match locale patterns like /fr_CA/, /nl_BE/, /en_US/ etc at the start of the path
  const localePrefixRegex = /^\/[a-z]{2}_[A-Z]{2}\//;
  return pagePath.replace(localePrefixRegex, '/');
};

// Forms the new url for the Country Selector
const getNewUrl = (stage: string, pagePath: string, newLocale?: LocaleDefault) => {
  if (!newLocale) return null;
  const newHost = getHostnameFromStageAndTLD(stage, newLocale.tld);

  // Strip any existing locale prefix from the pagePath
  const cleanPagePath = stripLocalePrefix(pagePath);

  if (newLocale.uses_language_subdirectory) {
    return `${newHost}/${newLocale.locale}${cleanPagePath}`;
  }
  return `${newHost}${cleanPagePath}`;
};

/**
 * Country Selector component
 * Handles the country selector dropdown and switch to the new locale
 * @param env - The environment data
 * @returns The Country Selector component
 */
export const CountrySelector: React.FC<CountrySelectorProps> = ({ env }) => {
  const options = useMemo(
    () => LOCALE_DEFAULTS.sort((a, b) => a.locale_name.localeCompare(b.locale_name)),
    [],
  );

  // Find the current selected country based on env
  const currentSelectedCountry = useMemo(() => {
    const found = LOCALE_DEFAULTS.find(
      (localeItem) => localeItem.locale === env.locale && localeItem.tld === env.tld,
    );
    return found;
  }, [env.locale, env.tld]);

  // Use the current country's name directly instead of managing separate state
  const selectedLang =
    currentSelectedCountry?.locale_name || LOCALE_DEFAULTS[LOCALE_DEFAULTS.length - 1].locale_name;

  // Handles the change event for the select element
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = LOCALE_DEFAULTS.find(
      (localeItem) => localeItem.locale_name === e.target.value,
    );
    const newUrl = getNewUrl(env.stage, env.pagePath, selectedOption);
    if (newUrl) {
      window.location.href = newUrl;
    }
  };

  return (
    <div className={styles.selector_container}>
      <select
        id="country-selector"
        aria-label="Select country or region"
        key={`${env.locale}-${env.tld}`}
        value={selectedLang}
        onChange={handleChange}
        className={styles.selector_button}
        data-heap-id={formatHeapId([ANALYTICS_HEAP_IDS.FOOTER_GENERAL, 'country-selector'])}
      >
        {options.map((option) => (
          <option key={option.locale_name} value={option.locale_name}>
            {option.locale_name}
          </option>
        ))}
      </select>
      <ArrowChevronDownIcon
        size="SM"
        color="var(--marm-color-action-fg-base)"
        className={styles.custom_arrow}
      />
    </div>
  );
};
